"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import Image from "next/image";
import { useTranslation } from "../language/hooks/useTranslation";

export default function Footer() {
  const pathname = usePathname();
  const isZh = pathname.startsWith("/zh");
  const { t } = useTranslation();

  return (
    <footer className="relative overflow-hidden border-t !bg-brand-bg text-brand-text border-brand-stroke px-4 sm:px-6 lg:px-8">

      <div className="lg:max-w-4xl xl:max-w-6xl 3xl:max-w-[1432px] relative !bg-brand-bg py-16 mx-auto">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-6 mb-14">
          <div className="sm:col-span-2 lg:col-span-2 space-y-6">
            <Link href={isZh ? "/zh" : "/"} className="inline-flex items-center">
              <Image
                src="/logo-tr.png"
                alt="Vell Logo"
                width={120}
                height={48}
                className="w-24"
              />
            </Link>
            <p className="max-w-sm text-sm 3xl:text-base leading-relaxed text-brand-muted">
              {t("footer.description")}
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Link
                href="https://apps.apple.com/au/app/vell/id6748893220"
                className="px-4 py-2 text-xs 3xl:text-sm transition border rounded-full bg-brand-card border-brand-stroke hover:border-brand-teal hover:text-brand-teal"
              >
                iOS
              </Link>
              <Link
                href="https://play.google.com/store/apps/details?id=com.remitcircle.app"
                className="px-4 py-2 text-xs 3xl:text-sm transition border rounded-full bg-brand-card border-brand-stroke hover:border-brand-teal hover:text-brand-teal"
              >
                Android
              </Link>
              <Link
                href="https://appgallery.huawei.com/#/app/C115220039"
                className="px-4 py-2 text-xs 3xl:text-sm transition border rounded-full bg-brand-card border-brand-stroke hover:border-brand-teal hover:text-brand-teal"
              >
                Huawei
              </Link>
            </div>
          </div>
          <div className="text-sm 3xl:text-base">
            <h3 className="mb-5 font-semibold tracking-wide text-brand-text">Products</h3>
            <ul className="space-y-3 list-none pl-0">
              <li>
                <Link href="/personal-account" className="text-brand-muted transition-colors hover:text-brand-teal">
                  Personal Account
                </Link>
              </li>
              <li>
                <Link href="/business-account" className="text-brand-muted transition-colors hover:text-brand-teal">
                  Business Account
                </Link>
              </li>
              <li>
                <Link href="/register" className="text-brand-muted transition-colors hover:text-brand-teal">
                  Open Account
                </Link>
              </li>
              <li>
                <Link href="/security-and-protection" className="text-brand-muted transition-colors hover:text-brand-teal">
                  Security
                </Link>
              </li>
              <li>
                <Link href="/resources/rateTracker" className="text-brand-muted transition-colors hover:text-brand-teal">
                  Rate Tracker
                </Link>
              </li>
              <li>
                <Link href="/resources/swift-code-finder" className="text-brand-muted transition-colors hover:text-brand-teal">
                  Swift Code Finder
                </Link>
              </li>
              <li>
                <Link href="/resources/international-money-transfer-comparison" className="text-brand-muted transition-colors hover:text-brand-teal">
                  Compare Providers
                </Link>
              </li>
            </ul>
          </div>
          <div className="text-sm 3xl:text-base">
            <h3 className="mb-5 font-semibold tracking-wide text-brand-text">Travel</h3>
            <ul className="space-y-3 list-none pl-0">
              <li>
                <Link href="/travel-esim" className="text-brand-muted transition-colors hover:text-brand-teal">
                  Our Plans
                </Link>
              </li>
              <li>
                <Link href="/travel-esim-comparison" className="text-brand-muted transition-colors hover:text-brand-teal">
                  Compare Plans
                </Link>
              </li>
              <li>
                <Link href="/travel-esim/install-esim" className="text-brand-muted transition-colors hover:text-brand-teal">
                  Installation Guide
                </Link>
              </li>
              <li>
                <Link href="/travel-esim/activate-esim" className="text-brand-muted transition-colors hover:text-brand-teal">
                  Activation Guide
                </Link>
              </li>
              <li>
                <Link href="/travel-esim/terms-and-conditions" className="text-brand-muted transition-colors hover:text-brand-teal">
                  eSIM Terms
                </Link>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div className="text-sm 3xl:text-base">
            <h3 className="mb-5 font-semibold tracking-wide text-brand-text">
              {t("footer.sections.company.title")}
            </h3>
            <ul className="space-y-3 list-none pl-0">
              <li>
                <Link href="/about-vell" className="text-brand-muted transition-colors hover:text-brand-teal">
                  {t("footer.sections.company.links.about")}
                </Link>
              </li>
              <li>
                <Link href="/career" className="text-brand-muted transition-colors hover:text-brand-teal">
                  Careers
                </Link>
              </li>
              <li>
                <Link href="/frequently-asked-questions" className="text-brand-muted transition-colors hover:text-brand-teal">
                  {t("footer.sections.company.links.faq")}
                </Link>
              </li>
              <li>
                <Link href="/contact-us" className="text-brand-muted transition-colors hover:text-brand-teal">
                  {t("footer.sections.company.links.contact")}
                </Link>
              </li>
              <li>
                <Link href="/site-map" className="text-brand-muted transition-colors hover:text-brand-teal">
                  Site Map
                </Link>
              </li>
              <li>
                <Link href="/blog" className="text-brand-muted transition-colors hover:text-brand-teal">
                  Blog
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div className="text-sm 3xl:text-base">
            <h3 className="mb-5 font-semibold tracking-wide text-brand-text">
              {t("footer.sections.legal.title")}
            </h3>
            <ul className="space-y-3 list-none pl-0">
              <li>
                <Link href="/legal/privacy-policy" className="text-brand-muted transition-colors hover:text-brand-teal">
                  {t("footer.sections.legal.links.privacy")}
                </Link>
              </li>
              <li>
                <Link href="/legal/cookie-policy" className="text-brand-muted transition-colors hover:text-brand-teal">
                  {t("footer.sections.legal.links.cookie")}
                </Link>
              </li>
              <li>
                <Link href="/legal/complaints-policy" className="text-brand-muted transition-colors hover:text-brand-teal">
                  {t("footer.sections.legal.links.complaints")}
                </Link>
              </li>
              <li>
                <Link href="/legal/terms-and-conditions" className="text-brand-muted transition-colors hover:text-brand-teal">
                  {t("footer.sections.legal.links.terms")}
                </Link>
              </li>
              <li>
                <Link href="/legal/regulatory" className="text-brand-muted transition-colors hover:text-brand-teal">
                  Regulatory Information
                </Link>
              </li>
            </ul>
          </div>

        </div>

        {/* Bottom bar */}
        <div className="flex flex-col items-center justify-between gap-4 pt-6 border-t border-brand-stroke md:flex-row">
          <p className="text-sm 3xl:text-base text-brand-muted">© 2026 Vell. All rights reserved.</p>
          <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs 3xl:text-sm text-brand-muted">
            <Link href="/site-map" className="transition-colors hover:text-brand-teal">Site Map</Link>
            <Link href="/legal/privacy-policy" className="transition-colors hover:text-brand-teal">Privacy</Link>
            <Link href="/legal/terms-and-conditions" className="transition-colors hover:text-brand-teal">Terms</Link>
            <Link href="/contact-us" className="transition-colors hover:text-brand-teal">Contact</Link>
          </div>
          <div className="flex gap-4">
            <Link href="https://www.instagram.com/vellglobal" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-brand-text transition-colors hover:text-brand-green">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
              </svg>
            </Link>
            <Link href="https://www.facebook.com/VellGlobal" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-brand-text transition-colors hover:text-brand-green">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </Link>
            <Link href="https://www.linkedin.com/company/vell-global" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="text-brand-text transition-colors hover:text-brand-green">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </Link>
            <Link href="https://www.youtube.com/@VELLglobal" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="text-brand-text transition-colors hover:text-brand-green">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.495 6.205a3.007 3.007 0 00-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 00.527 6.205a31.247 31.247 0 00-.522 5.805 31.247 31.247 0 00.522 5.783 3.007 3.007 0 002.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 002.088-2.088 31.247 31.247 0 00.5-5.783 31.247 31.247 0 00-.5-5.805zM9.609 15.601V8.408l6.264 3.602z"/>
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
